import {useBus} from "@/core/store/bus";
import {ValidateResult, ValidateRules} from "@/core/types";
import {arrayIndex} from "@/core/util/arrayIndex";
import {templater} from "@/core/util/templater";
import toArray from "@/core/util/toArray";
import validateRulesFirst from "@/core/util/validate/validateRulesFirst";
import {enumErrorNameEnum, ErrorNameEnum, VorderError, VOrderSectionEnum} from "@/gql/gen";
import {useCompanyStore} from "@/modules/company/store/company";
import {useFortuneStore} from "@/modules/fortune/store/fortune";
import {useBasketStore} from "@/modules/shop/store/basket";
import {OrderAttributesChanged, OrderAttributesChangedValues} from "@/modules/shop/store/util/vorder";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {checkOrderRestriction, isBasketItemRestricted} from "@/modules/shop/util/checkOrderRestriction";
import {defineStore, storeToRefs} from "pinia";
import {computed, ref} from "vue";
import {useDeliveryStore} from "@/modules/shop/store/delivery";

const STORE_NAME = "vorder-validate";

export const useVorderValidate = defineStore(STORE_NAME, () => {

    const {bus} = useBus()
    const basketStore = useBasketStore()
    const companyStore = useCompanyStore()
    const vorderState = useVorderState()
    const deliveryStore = useDeliveryStore()
    const {deliveryCalculateResult, orderOffice} = storeToRefs(vorderState);
    const {deliveryCalculateResultActual} = storeToRefs(deliveryStore);

    const {
        attrs,
        orderRestrictions,
    } = storeToRefs(vorderState);

    const basketItems = computed(() => basketStore.basketItemsModels || []);
    const basketItemsStat = computed(() => basketStore.basketItemsStat || {});

    const {officesById} = storeToRefs(companyStore);

    // Получаем ID выбранного приза из fortune store (ленивая загрузка для избежания циклических зависимостей)
    const userPrizeElementId = computed(() => {
        try {
            const fortuneStore = useFortuneStore()
            return fortuneStore.userPrizeElement?.ID ?? null
        } catch {
            return null
        }
    });


    const getRulesPickup = () => {
        const res: ValidateRules = []
        res.push(() => {
            return !!attrs.value.PICKUP_DEPARTMENT || 'Не выбрано подразделение'
        })
        return res
    }

    const getRulesDelivery = () => {
        const res: ValidateRules = [
            () => !!attrs.value.ADDRESS || 'Не указан адрес доставки',
            () => !!attrs.value.HOUSE || 'Не указан дом'
        ]
        return res
    }

    const getRulesTime = () => {
        const res: ValidateRules = [
            () => !!attrs.value['DATE'] || !!attrs.value['TIME'] || 'не выбрана дата и время',
            () => !!attrs.value['DATE'] || 'не выбрана дата',
            () => !!attrs.value['TIME'] || 'не выбрано время',
            () => {
                if (!deliveryCalculateResult.value)
                    return 'время не проверено'
                switch (deliveryCalculateResult.value?.RES_STATUS) {
                    case 'time_busy':
                        return 'время занято';
                    case 'service_unavailable':
                        return 'не удалось проверить время, попробуйте снова';
                    case 'address_not_found':
                        return 'адрес не найден';
                }
                if (!deliveryCalculateResultActual.value)
                    return 'необходимо перепроверить вермя'
                return true
            },
            () => !!deliveryCalculateResult.value || 'время не проверено',
        ]
        return res
    }

    const getRulesPayment = () => {
        const res: ValidateRules = [
            () => !!attrs.value['PAYMENT_TYPE'] || 'не выбран способ оплаты',
        ]
        return res
    }

    const getRulesBasket = () => {
        const res: ValidateRules = [
            () => !!basketStore.basketItems.length || 'корзина пуста',
        ]
        return res
    }

    const getRulesBenefit = () => {
        const res: ValidateRules = [
            //() => 'ошибка бенефита',
        ]
        return res
    }

    /**
     * Проверяет ограничения заказа и возвращает массив ошибок
     */
    const getRestrictionErrors = (): VorderError[] => {
        const result: VorderError[] = [];

        // Если корзина пустая, не проверяем ограничения
        if (!basketItems.value.length) {
            return result;
        }

        orderRestrictions.value.forEach((restriction) => {
            // Проверяем применимость ограничения к текущему заказу
            const checkResult = checkOrderRestriction(restriction.CONDITION, {
                attrs: attrs.value,
                schedule: restriction.SCHEDULE,
                office: orderOffice.value,
                basketStat: basketItemsStat.value,
                basketItems: basketItems.value,
                prizeId: userPrizeElementId.value,
                couponId: vorderState.coupon?.ID ?? null,
            });

            // Если ограничение не применимо, пропускаем
            if (!checkResult.isApplicable) {
                return;
            }

            const errorSection = restriction.SECTION;

            // Добавляем ошибки для конкретных товаров в корзине
            if (checkResult.matchedProductIds.length > 0 || checkResult.matchedSectionIds.length > 0) {
                basketItems.value.forEach((basketItem) => {
                    if (isBasketItemRestricted(
                        basketItem,
                        checkResult.matchedProductIds,
                        checkResult.matchedSectionIds
                    )) {
                        const itemMessage = restriction.ITEM_MESSAGE || '';
                        const message = templater(itemMessage, checkResult.vars);
                        const errorName = restriction.ITEM_ERROR_NAME ||
                            enumErrorNameEnum.vorder_basket_item_restricted;

                        result.push({
                            __typename: 'VorderError',
                            name: errorName as ErrorNameEnum,
                            message,
                            itemUuid: basketItem.getUuid(),
                            section: 'basket_item',
                            alert: false,
                            alertDuration: null,
                            data: null,
                            debug: null,
                            fieldLabel: null,
                            fieldMessage: null,
                            fieldName: null,
                            messages: [],
                            rel: null,
                            type: null
                        });
                    }
                });
            }

            // Добавляем общую ошибку ограничения
            const message = templater(restriction.MESSAGE || '', checkResult.vars);
            result.push({
                __typename: 'VorderError',
                name: enumErrorNameEnum.vorder_restriction,
                message,
                section: errorSection as any,
                alert: null,
                alertDuration: null,
                data: null,
                debug: null,
                fieldLabel: null,
                fieldMessage: null,
                fieldName: null,
                itemUuid: null,
                messages: [],
                rel: null,
                type: null
            });
        });

        return result;
    };

    const validateResult = ref<VorderError[]>([])

    const validateResultBySection = computed<Record<VOrderSectionEnum, VorderError>>(() => {
        return arrayIndex(validateResult.value, 'section')
    })

    const validateResultCommon = computed<VorderError[]>(() => {
        const sections: VOrderSectionEnum[] = ['common', 'basket', 'benefit']
        return validateResult.value
            .filter(item => sections.includes(item.section as VOrderSectionEnum))
            .slice(0, 2)
    })

    const validateResultBasketItems = computed<VorderError[]>(() => {
        const sections: VOrderSectionEnum[] = ['basket_item']
        return validateResult.value
            .filter(item => sections.includes(item.section as VOrderSectionEnum))
            .slice(0, 1)
    })

    const validateSectionRules = (section: VOrderSectionEnum, onlyFilled: boolean, errors: Partial<VorderError>[], restrictionErrors?: VorderError[]) => {

        let res: ValidateResult = []

        switch (section) {
            case 'delivery_courier':
                if (onlyFilled && !attrs.value.ADDRESS) {
                    return;
                }
                res = validateRulesFirst(null, getRulesDelivery())
                break;
            case 'delivery_pickup':
                if (onlyFilled && !attrs.value.PICKUP_DEPARTMENT) {
                    return;
                }
                res = validateRulesFirst(null, getRulesPickup())
                break;
            case 'payment':
                if (onlyFilled && !attrs.value.PAYMENT_TYPE) {
                    return;
                }
                res = validateRulesFirst(null, getRulesPayment())
                break;
            case 'time':
                if (onlyFilled && (!attrs.value.TIME || !attrs.value.DATE)) {
                    return;
                }
                res = validateRulesFirst(null, getRulesTime())
                break;
            case 'benefit':
                if (onlyFilled && !attrs.value.BENEFIT_TYPE) {
                    return;
                }
                res = validateRulesFirst(null, getRulesBenefit())
                break;
            case 'basket':
                res = validateRulesFirst(null, getRulesBasket())
                break;
            case "basket_item":
                basketItems.value.forEach((basketItem) => {
                    if (basketItem.fields.QUANTITY > 2) {
                        /*
                        errors.push({
                            section: 'basket_item',
                            itemUuid: basketItem.getUuid(),
                            message: 'Больше чем 2'
                        })
                         */
                    }
                })
                break;
        }

        if (res && res !== true && Array.isArray(res)) {
            const error = res[0]
            if (error)
                errors.push({
                    section,
                    message: error.message
                })
        }

        if (restrictionErrors) {
            restrictionErrors.filter(error => error.section === section).forEach((error) => {
                errors.push(error)
            })
        }

        return res
    }

    const validate = (section?: VOrderSectionEnum | VOrderSectionEnum[] | null, onlyFilled: boolean = false) => {
        const sections = section ? toArray(section) : []
        let restrictionErrors = getRestrictionErrors()

        const result: Partial<VorderError>[] = []

        if (sections.length) {
            for (const sect of sections) {
                validateSectionRules(sect, onlyFilled, result, restrictionErrors)
            }
            validateResult.value = validateResult.value.filter(error => !sections.includes(error.section!))
            validateResult.value.push(...result as any)
        } else {
            validateResult.value = []

            if (attrs.value.TRANSPORT_TYPE === 'courier') {
                validateSectionRules('delivery_courier', onlyFilled, result, restrictionErrors)
            } else {
                validateSectionRules('delivery_pickup', onlyFilled, result, restrictionErrors)
            }

            validateSectionRules('time', onlyFilled, result, restrictionErrors)
            validateSectionRules('benefit', onlyFilled, result, restrictionErrors)
            validateSectionRules('payment', onlyFilled, result, restrictionErrors)
            validateSectionRules('basket', onlyFilled, result, restrictionErrors)
            validateSectionRules('basket_item', onlyFilled, result, restrictionErrors)

            validateResult.value = result as any
        }

        return !validateResult.value.length
    };

    const resetValidation = (section?: string | string[]) => {
        const sections = section ? toArray(section) : []
        if (sections.length) {
            validateResult.value = validateResult.value.filter(error => sections.includes(error.section as string))
        } else {
            validateResult.value = []
        }
    }

    bus.on('vorder:basket.change', () => {
        validate(['basket', 'basket_item'])
    })

    bus.on('vorder:attrs.change', (attrsChanged: OrderAttributesChanged, attrsChangedValue: OrderAttributesChangedValues) => {
        if (
            attrsChanged.TRANSPORT_TYPE ||
            attrsChanged.PAYMENT_TYPE ||
            attrsChanged.PICKUP_DEPARTMENT
        ) {
            validate(null, true)
        }
    })

    return {
        getRestrictionErrors,
        validateResult,
        validateResultBySection,
        validateResultCommon,
        validateResultBasketItems,
        validate,
        resetValidation
    };
});
